class GooglePixelService {
  constructor() {
    this.pixelId = null;
    this.initialized = false;
    this.events = [];
  }

  // Initialize Google Pixel for authenticated Google brands
  initialize(pixelId, options = {}) {
    if (!pixelId) {
      console.warn('Google Pixel ID is required');
      return false;
    }

    this.pixelId = pixelId;
    
    try {
      // Load Google Analytics 4 / Google Ads tracking
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${pixelId}`;
      document.head.appendChild(script);

      // Initialize gtag
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };

      window.gtag('js', new Date());
      window.gtag('config', pixelId, {
        send_page_view: options.sendPageView !== false,
        cookie_flags: 'SameSite=None;Secure',
        ...options
      });

      this.initialized = true;
      
      // Send any queued events
      this.events.forEach(event => {
        this.trackEvent(event.action, event.parameters);
      });
      this.events = [];

      console.log(`Google Pixel initialized with ID: ${pixelId}`);
      return true;
    } catch (error) {
      console.error('Failed to initialize Google Pixel:', error);
      return false;
    }
  }

  // Track custom events for campaigns and posts
  trackEvent(action, parameters = {}) {
    if (!this.initialized) {
      // Queue events if pixel not yet initialized
      this.events.push({ action, parameters });
      return;
    }

    try {
      window.gtag('event', action, {
        event_category: parameters.category || 'engagement',
        event_label: parameters.label || '',
        value: parameters.value || 0,
        custom_parameters: {
          campaign_id: parameters.campaignId,
          post_id: parameters.postId,
          business_id: parameters.businessId,
          platform: parameters.platform,
          content_type: parameters.contentType,
          ...parameters.custom
        }
      });

      console.log(`Google Pixel event tracked: ${action}`, parameters);
    } catch (error) {
      console.error('Failed to track Google Pixel event:', error);
    }
  }

  // Track campaign-specific events
  trackCampaignEvent(eventType, campaignData) {
    const parameters = {
      category: 'campaign',
      campaignId: campaignData.id,
      businessId: campaignData.businessId,
      platform: campaignData.platform,
      contentType: campaignData.contentType,
      label: `${eventType}_${campaignData.name || 'unnamed'}`,
      custom: {
        campaign_name: campaignData.name,
        target_audience: campaignData.targetAudience,
        budget: campaignData.budget,
        objective: campaignData.objective
      }
    };

    this.trackEvent(eventType, parameters);
  }

  // Track post creation events
  trackPostEvent(eventType, postData) {
    const parameters = {
      category: 'social_post',
      postId: postData.id,
      businessId: postData.businessId,
      platform: postData.platform,
      contentType: postData.contentType,
      label: `${eventType}_${postData.platform}`,
      custom: {
        post_type: postData.type,
        has_image: postData.hasImage,
        has_video: postData.hasVideo,
        scheduled: postData.scheduled,
        character_count: postData.content?.length || 0
      }
    };

    this.trackEvent(eventType, parameters);
  }

  // Track conversion events for business metrics
  trackConversion(conversionType, value = 0, currency = 'USD') {
    this.trackEvent('conversion', {
      category: 'conversion',
      label: conversionType,
      value: value,
      custom: {
        conversion_type: conversionType,
        currency: currency,
        conversion_time: new Date().toISOString()
      }
    });
  }

  // Set user properties for better targeting
  setUserProperties(properties) {
    if (!this.initialized) {
      console.warn('Google Pixel not initialized');
      return;
    }

    try {
      window.gtag('config', this.pixelId, {
        custom_map: properties
      });
    } catch (error) {
      console.error('Failed to set user properties:', error);
    }
  }

  // Enhanced e-commerce tracking for business metrics
  trackPurchase(transactionData) {
    if (!this.initialized) return;

    try {
      window.gtag('event', 'purchase', {
        transaction_id: transactionData.id,
        value: transactionData.value,
        currency: transactionData.currency || 'USD',
        items: transactionData.items || []
      });
    } catch (error) {
      console.error('Failed to track purchase:', error);
    }
  }

  // Check if pixel is properly initialized
  isInitialized() {
    return this.initialized && this.pixelId;
  }

  // Get current pixel configuration
  getConfig() {
    return {
      pixelId: this.pixelId,
      initialized: this.initialized,
      queuedEvents: this.events.length
    };
  }

  // Reset pixel service
  reset() {
    this.pixelId = null;
    this.initialized = false;
    this.events = [];
  }
}

const googlePixelService = new GooglePixelService();
export default googlePixelService;